import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { gnf } from '@/lib/invoices';
import { MISSION_STATUS_LABELS, MISSION_STATUS_VARIANT } from '@/lib/missions';
import { useHasPermission } from '@/lib/permissions';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';

import { OffsetPager } from './OffsetPager';
import { SummaryTile } from './SummaryTile';

import type { Mission } from '@logistech/ui-shared';

/** GET /missions/vehicle-summary/:vehicleId — revenueGnf absent sans invoices.read */
export interface VehicleMissionsSummary {
  totalMissions: number;
  missionsThisMonth: number;
  revenueGnf?: number;
}

/** GET /missions?vehicleId= (missions-service) : limit/offset, pas le Paginated<T> page/pageSize (TD-034) */
export interface VehiclePaginatedMissions {
  data: Mission[];
  total: number;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 20;

export function VehicleMissionsTab({ vehicleId }: { vehicleId: string }) {
  const navigate = useNavigate();
  const canReadInvoices = useHasPermission('invoices.read');
  const [offset, setOffset] = useState(0);

  const summaryQuery = useQuery({
    queryKey: ['vehicle-missions-summary', vehicleId],
    queryFn: async () => (await api.get<VehicleMissionsSummary>(`/missions/vehicle-summary/${vehicleId}`)).data,
  });
  const listQuery = useQuery({
    queryKey: ['vehicle-missions', vehicleId, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ vehicleId, limit: String(PAGE_SIZE), offset: String(offset) });
      return (await api.get<VehiclePaginatedMissions>(`/missions?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
  const summary = summaryQuery.data;
  const list = listQuery.data;
  // Champ absent de la réponse sans invoices.read : on ne l'affiche jamais à 0
  const showRevenue = canReadInvoices && summary?.revenueGnf !== undefined;

  return (
    <div className="space-y-4">
      <QueryError query={summaryQuery} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryTile label="Missions" value={String(summary?.totalMissions ?? 0)} sub="au total" loading={summaryQuery.isLoading} />
        <SummaryTile label="Ce mois-ci" value={String(summary?.missionsThisMonth ?? 0)} sub="planifiées ce mois" loading={summaryQuery.isLoading} />
        {showRevenue && (
          <SummaryTile label="Chiffre d'affaires" value={gnf(summary.revenueGnf!)} sub="HT, factures émises" />
        )}
      </div>

      <QueryError query={listQuery} />
      <div className="bg-card rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Référence</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Rotations</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listQuery.isLoading && Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 5 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {list?.data.map((m) => (
              <TableRow
                key={m.id}
                className="cursor-pointer"
                onClick={() => void navigate({ to: '/missions/$id', params: { id: m.id } })}
              >
                <TableCell className="font-mono font-semibold">
                  <Link to="/missions/$id" params={{ id: m.id }} onClick={(e) => e.stopPropagation()}>{m.reference}</Link>
                </TableCell>
                <TableCell>{m.client?.name ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={MISSION_STATUS_VARIANT[m.status]}>{MISSION_STATUS_LABELS[m.status] ?? m.status}</Badge>
                </TableCell>
                <TableCell>{new Date(m.scheduledAt ?? m.createdAt).toLocaleDateString('fr-FR')}</TableCell>
                <TableCell className="text-right tabular-nums">{m.rotationsSummary?.count ?? 0}</TableCell>
              </TableRow>
            ))}
            {list && list.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucune mission pour ce véhicule</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {list && <OffsetPager total={list.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />}
      </div>
    </div>
  );
}
